<template>
  <v-container fluid class="pa-0">
    <!-- Hero Section -->
    <v-container class="hero-section py-12 py-md-16" style="background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="text-center mb-8">
            <h1 class="text-h3 text-md-h2 font-weight-bold mb-4" style="color: white;">
              <v-icon size="48" class="mb-2" color="white">mdi-flower</v-icon>
              <br>
              Cemetery Community Genealogy
            </h1>
            <p class="text-h6 text-md-h5 font-weight-light mb-6" style="color: rgba(255, 255, 255, 0.9);">
              Honoring memories, preserving legacies
            </p>
            <p class="text-body-1 mx-auto" style="max-width: 600px; color: rgba(255, 255, 255, 0.95);">
              Search cemetery records from our community to find information about your loved ones. 
              Access official burial records, locations, and memorial information all in one place.
            </p>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Search Section -->
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <v-card elevation="8" class="rounded-lg">
            <v-card-title class="text-h5 pa-6 pb-4">
              <v-icon class="mr-2" color="primary">mdi-magnify</v-icon>
              Search Records
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-6">
              <div class="text-body-2 text-medium-emphasis mb-4">
                <v-icon size="small" class="mr-1">mdi-information</v-icon>
                Enter a first name, last name, or both to search across all cemeteries in our community.
              </div>
              <SearchComponent />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- How It Works Section -->
    <v-container class="py-12">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <h2 class="text-h4 text-center mb-8 font-weight-bold">How It Works</h2>
          <v-row>
            <v-col cols="12" md="4">
              <v-card class="text-center pa-6 h-100" elevation="2">
                <v-avatar size="64" color="primary" class="mb-4">
                  <v-icon size="32" color="white">mdi-numeric-1</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-3">Search</h3>
                <p class="text-body-2 text-medium-emphasis">
                  Enter your loved one's name in the search box above to find their records
                </p>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card class="text-center pa-6 h-100" elevation="2">
                <v-avatar size="64" color="primary" class="mb-4">
                  <v-icon size="32" color="white">mdi-numeric-2</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-3">Browse</h3>
                <p class="text-body-2 text-medium-emphasis">
                  View detailed information including burial location, dates, and cemetery details
                </p>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card class="text-center pa-6 h-100" elevation="2">
                <v-avatar size="64" color="primary" class="mb-4">
                  <v-icon size="32" color="white">mdi-numeric-3</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-3">Request Updates</h3>
                <p class="text-body-2 text-medium-emphasis">
                  Found an error? Submit an edit request to help keep records accurate
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <!-- Cemetery Directory Section -->
    <v-container class="py-12">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <div class="text-center mb-8">
            <h2 class="text-h4 font-weight-bold mb-3">
              <v-icon class="mr-2" color="primary">mdi-map-marker-multiple</v-icon>
              Our Cemetery Community
            </h2>
            <p class="text-body-1 text-medium-emphasis">
              Browse records from {{ cemeteries.length }} {{ cemeteries.length === 1 ? 'cemetery' : 'cemeteries' }} in our network
            </p>
          </div>

          <v-skeleton-loader v-if="loading" type="card, card" class="mb-4"></v-skeleton-loader>

          <v-row v-else>
            <v-col v-for="cemetery in cemeteries" :key="cemetery.id" cols="12" md="6">
              <v-card 
                :href="'/cemetery/' + cemetery.genealogyslug"
                class="cemetery-card h-100" 
                elevation="2"
                hover
              >
                <v-card-title class="text-h6 pb-2">
                  <v-icon class="mr-2" size="small">mdi-grave-stone</v-icon>
                  {{ cemetery.name }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <div class="text-body-2">
                    <div v-if="cemetery.address" class="mb-1">
                      <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                      {{ cemetery.address }}
                    </div>
                    <div v-if="cemetery.addresstwo" class="mb-1 ml-6">
                      {{ cemetery.addresstwo }}
                    </div>
                    <div v-if="cemetery.addressthree" class="mb-1 ml-6">
                      {{ cemetery.addressthree }}
                    </div>
                    <div v-if="cemetery.city && cemetery.state && cemetery.zipcode" class="ml-6">
                      {{ cemetery.city }}, {{ cemetery.state }} {{ cemetery.zipcode }}
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn 
                    variant="text" 
                    color="primary"
                    append-icon="mdi-arrow-right"
                  >
                    View Records
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <!-- Contact/Membership Section -->
    <v-container class="py-12">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8">
          <v-card class="pa-6" elevation="4">
            <v-card-title class="text-h5 pb-4">
              <v-icon class="mr-2" color="primary">mdi-account-group</v-icon>
              Join Our Community
            </v-card-title>
            <v-card-text>
              <p class="text-body-1 mb-4">
                Is your cemetery interested in becoming a member of our community? 
                We help preserve and share cemetery records to honor those who came before us.
              </p>
              <v-row>
                <v-col cols="12" md="6">
                  <v-btn
                    variant="outlined"
                    color="primary"
                    size="large"
                    block
                    prepend-icon="mdi-phone"
                    href="tel:8103283419"
                  >
                    Call: 810.328.3419
                  </v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <v-btn
                    variant="outlined"
                    color="primary"
                    size="large"
                    block
                    prepend-icon="mdi-email"
                    href="mailto:sales@danstechservices.us"
                  >
                    Email Us
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import SearchComponent from '@/components/SearchComponent.vue';
import { defineComponent } from 'vue';

// Components

export default defineComponent({
  name: 'HomeView',

  components: {
    SearchComponent
  },

  data: () => ({
    cemeteries : [],
    loading : true
  }),

  created : function(){
    this.getCemeteries();
    this.pageView();
  },

  methods:{
    pageView(){
      fetch("https://app.cemeterycommunity.com:11000/api/pageview", { method: "POST", headers : { data : JSON.stringify( { cemeteryid : null, group : 'Genealogy', page : '/home' } ) } });
    },
    getCemeteries(){
      this.cemeteries = [];

      fetch("https://app.cemeterycommunity.com:11000/api/getgenealogycemeteries", { method: "POST" })
      .then( res => res.json() )
      .then( res =>  {
        if(res.results != "Failed" && res.results.length > 0) this.cemeteries = res.results;

        this.loading = false;
      });
    }
  }
});
</script>
